import { Typography } from "@progress/kendo-react-common/dist/es/typography/Typography";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { DataResult, State } from "@progress/kendo-data-query";
import { GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { InputChangeEvent } from "@progress/kendo-react-inputs/dist/npm/input/interfaces/InputChangeEvent";
import { useTheme } from "@mui/material";
import { DockForFtlRouteViewModel } from "../../../../ViewModel/DockForFtlRouteViewModel";
import { EditProjectContext } from "../../../EditProjectContext";
import InlineEditProps from "../../../../../Grid/InlineEdit/InlineEditProps";
import CommandColumn from "../../../../../Grid/ControlColumn/CommandColumn";
import { CommandDeleteRowButton } from "../../../../../Grid/ControlColumn/CommandDeleteRowButton";
import { ProjectState } from "../../../../ViewModel/ProjectState";
import StyledKendoGrid from "../../../../../Grid/StyledKendoGrid";
import { PlantDockGridColumns } from "./PlantDockGridColumns";
import { ToggleButton } from "./ToggleButton";
import { StyledFlexAlignEnd } from "../FtlRouteHeader/FtlRouteHeader";
import TextInput from "../../../../../Layout/Inputs/TextInput";
import { addDockInputOnClick } from "./AddDockInputOnClick";
import { deletePlantDockOnClick } from "./DeletePlantDockOnClick";
import { setDockOnRoute } from "./SetDockOnRoute";
import { isProjectReadOnly } from "../../../IsProjectReadOnly";

type Props = {
  docks: Array<DockForFtlRouteViewModel>;
  ftlRouteId: number;
};

export default function PlantDocksForLtlPipelineGrid({ docks, ftlRouteId }: Props) {
  const { project } = useContext(EditProjectContext);
  const editProjectContext = useContext(EditProjectContext);
  const isReadonly = isProjectReadOnly(project);
  const initialState: State = {};
  const idField = "DockId";
  const disabled = project.State !== ProjectState.Sandbox;
  const [plantDock, setPlantDock] = React.useState("");
  const [dataResult, setDataResult] = React.useState<DataResult>({
    data: docks,
    total: docks.length,
  });

  const [dataState, setDataState] = React.useState<State>(initialState);
  useEffect(() => {
    setDataResult({
      data: docks,
      total: docks.length,
    });
  }, [docks, project]);

  const [expanded, setExpanded] = useState<Array<number>>([]);

  const toggleCardExpansion = (panel: number) => {
    if (expanded.includes(panel)) {
      const array = [...expanded]; // make a separate copy of the array
      const index = array.indexOf(panel);
      if (index !== -1) {
        array.splice(index, 1);
        setExpanded(array);
      }
    } else {
      setExpanded([...expanded, panel]);
    }
  };

  const onDataResultChanged = (updatedDataResult: DataResult) => {
    setDataResult(updatedDataResult);
    setDockOnRoute(ftlRouteId, updatedDataResult.data, editProjectContext);
  };

  const [editState, setEditState] = React.useState<number>();
  const inlineEditProps = InlineEditProps({
    idField,
    dataResult,
    onDataReceived: onDataResultChanged,
    setEditState,
    isReadonly,
  });

  const commandColumn = CommandColumn({
    // eslint-disable-next-line react/no-unstable-nested-components
    commands: (dataItem: any) => [
      <CommandDeleteRowButton
        disabled={disabled}
        key="CommandRemoveButton"
        onClick={() =>
          deletePlantDockOnClick(
            dataItem,
            ftlRouteId,
            idField,
            dataResult,
            onDataResultChanged,
            project.ProjectId
          )
        }
      />,
    ],
  });

  const handlePlantDockInput = (event: InputChangeEvent) => {
    setPlantDock(event.target.value as string);
  };

  const InputRow = isReadonly ? (
    <div />
  ) : (
    <StyledFlexAlignEnd style={{ marginBottom: "1.5rem" }}>
      <TextInput
        label="Plant GSDB.Dock Code"
        onChange={handlePlantDockInput}
        name="plantDock"
        value={plantDock}
        disabled={isReadonly}
        style={{ width: "202px" }}
      />
      {isReadonly ? (
        <Button disabled>
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      ) : (
        <Button onClick={() => addDockInputOnClick(plantDock, ftlRouteId, editProjectContext)}>
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      )}
    </StyledFlexAlignEnd>
  );

  const theme = useTheme();
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      {/* eslint-disable-next-line react/jsx-pascal-case */}
      <Typography.h4>
        Plant/Dock For LTL Inbound Routing
        <div
          style={{
            display: "inline-block",
            float: "right",
          }}
        />
        <ToggleButton
          onClick={() => toggleCardExpansion(ftlRouteId)}
          expanded={expanded.includes(ftlRouteId)}
        />
      </Typography.h4>

      {expanded.includes(ftlRouteId) && (
        <>
          {InputRow}
          <StyledKendoGrid
            muiTheme={theme}
            resizable
            sortable
            data={{
              data: dataResult.data
                .filterBy(dataState)
                .orderBy(dataState)
                .withEditfield(idField, editState),
              total: dataResult.total,
            }}
            dataItemKey={idField}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
            {...inlineEditProps}
            style={{ width: "530px" }}
          >
            {[...PlantDockGridColumns(), commandColumn]}
          </StyledKendoGrid>
        </>
      )}
    </div>
  );
}
